// React and Modules
import { useState, useEffect, useContext } from 'react';
import logError from '../../../utils/errorUtil.js';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

// Utils
import { useAuth } from '../../context/wms-context.js';
import LoadingSpinner from '../../../components/LoadingSpinner';
import BlockingTooltip from '../../../components/BlockingTooltip';

// Other Pages
import Physical from './Physical';
import Forms from './forms/Forms.js';
import Trace from './Trace';
import { pageNames } from './utils/data';
import { createAirCertAtaRequest, getReleaseLineData, printAllPickingLabels } from './utils/guided-interface-functions';
import { GuidedInterfaceContext } from './context/GuidedInterfaceContext.js';
import { FormContext } from './context/FormContextWrapper.js';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

const ataRequestModalStatuses = {
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
};

const defaultAtaRequestModal = {
    shown: false,
    status: null,
    releaseLink: null,
    newReleaseName: null,
    errorMessage: '',
    releaseLineCount: 0,
};

export default function ReleaseLines({ releaseName, clearSelectedRelease, releaseData }) {
    const { currentUser } = useAuth();
    const { setShowNotification, startTimer, stopTimer } = useContext(GuidedInterfaceContext);
    const { setCurrentRelease } = useContext(FormContext);

    const [selectedPage, setSelectedPage] = useState(pageNames.RELEASE_LINES);
    const [releaseLineData, setReleaseLineData] = useState([]);
    const [internalComments, setInternalComments] = useState([]);
    const [loadingReleaseLines, setLoadingReleaseLines] = useState(false);
    const [currentReleaseLine, setCurrentReleaseLine] = useState(null);
    const [pickingLabelsLoading, setPickingLabelsLoading] = useState(false);
    const [documentsToPrint, setDocumentsToPrint] = useState([]);

    const [selectedAtaReleaseLineNames, setSelectedAtaReleaseLineNames] = useState([]);
    const [ataRequestSubmitting, setAtaRequestSubmitting] = useState(false);
    const [ataRequestModal, setAtaRequestModal] = useState(defaultAtaRequestModal);
    const [sendAtaRequestEnabled, setSendAtaRequestEnabled] = useState(false);
    const hasATAPermissions =
        sendAtaRequestEnabled && currentUser.permissions.includes('ATA-Request') && releaseData?.salesOrderName;

    useEffect(() => {
        const timer = startTimer();
        if (!releaseLineData?.length) {
            getReleaseLines();
            setCurrentRelease(releaseName);
        }
        return () => stopTimer(timer);
    }, []);

    const returnToReleaseLines = () => {
        handlePageChange(pageNames.RELEASE_LINES);
        getReleaseLines();
        setCurrentReleaseLine(null);
    };

    async function getReleaseLines(showError = true) {
        setLoadingReleaseLines(true);
        try {
            const response = await getReleaseLineData(currentUser, releaseName);
            const formattedResponse = response.releaseLines;
            setSendAtaRequestEnabled(response.sendAtaRequestEnabled);

            if (!formattedResponse?.length) {
                if (showError) {
                    setShowNotification({
                        shown: true,
                        status: 'error',
                        title: 'No Valid Release Lines',
                        message: 'No valid allocated Inventory Lines found on Release',
                    });
                    clearSelectedRelease();
                } else {
                    setReleaseLineData([]);
                    setInternalComments([]);
                }
                return;
            }

            setReleaseLineData(formattedResponse);
            setInternalComments(formattedResponse[0].releaseHeaderInternalComments);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error Loading Release Lines',
                message:
                    'There was an error loading the release lines. Please try again and if this issue persists contact your manager.',
            });
            logError(error, 'Error getting release line data:');
            clearSelectedRelease();
        } finally {
            setLoadingReleaseLines(false);
        }
    }

    const handlePageChange = (page, lineId) => {
        setSelectedPage(page);

        const matchingLine = releaseLineData.find((line) => line.releaseLineId === lineId);
        setCurrentReleaseLine(matchingLine);
    };

    const getPickingLabelsToPrint = async () => {
        try {
            setPickingLabelsLoading(true);
            await printAllPickingLabels(currentUser, releaseName);
        } catch (error) {
            setShowNotification({
                shown: true,
                error: 'error',
                title: 'Error Loading Picking Labels',
                message: `There was an error loading the picking labels for ${releaseName}. Please try again and if this issue persists contact your manager.`,
            });
        } finally {
            setPickingLabelsLoading(false);
        }
    };

    const processedAmount = () => {
        let processed = 0;
        releaseLineData.forEach((line) => {
            if (line.physicalStatus && line.traceStatus && line.formsStatus) {
                processed++;
            }
        });

        return `${processed} out of ${releaseData.inventoryLineCount}`;
    };

    const handleATACheckbox = (releaseLineName, isSelected) => {
        setSelectedAtaReleaseLineNames((prev) => {
            if (!isSelected) {
                return prev.filter((selectedReleaseLineName) => {
                    return selectedReleaseLineName !== releaseLineName;
                });
            }

            if (!prev.includes(releaseLineName)) {
                return [...prev, releaseLineName];
            }

            return prev;
        });
    };

    const submitATARequestToAirCert = async () => {
        const releaseLineNamesToSubmit = [...selectedAtaReleaseLineNames];

        try {
            setAtaRequestSubmitting(true);
            setAtaRequestModal({
                shown: true,
                status: ataRequestModalStatuses.LOADING,
                releaseLink: null,
                newReleaseName: null,
                errorMessage: '',
                releaseLineCount: releaseLineNamesToSubmit.length,
            });

            const ataRequestResponse = await createAirCertAtaRequest(currentUser, releaseLineNamesToSubmit);
            setSelectedAtaReleaseLineNames([]);
            setAtaRequestModal({
                shown: true,
                status: ataRequestModalStatuses.SUCCESS,
                releaseLink: ataRequestResponse.newReleaseLink,
                newReleaseName: ataRequestResponse.newReleaseName,
                errorMessage: '',
                releaseLineCount: releaseLineNamesToSubmit.length,
            });

            // Open the ATA Release Router PDF in a new tab if the server returned one
            if (ataRequestResponse.ataPdf?.data) {
                const blob = new Blob([new Uint8Array(ataRequestResponse.ataPdf.data).buffer], { type: 'application/pdf' });
                const pdfUrl = window.URL.createObjectURL(blob);
                window.open(pdfUrl, '_blank');
            }
        } catch (error) {
            logError(error, 'submitATARequestToAirCert');
            setAtaRequestModal({
                shown: true,
                status: ataRequestModalStatuses.ERROR,
                releaseLink: null,
                newReleaseName: null,
                errorMessage:
                    error?.message ||
                    `There was an error sending the ATA request for ${releaseName}. Please try again and if this issue persists contact your manager.`,
                releaseLineCount: releaseLineNamesToSubmit.length,
            });
        } finally {
            setAtaRequestSubmitting(false);
        }
    };

    const clickableAction = (actionType) => {
        if (!actionType) {
            return <ExclamationCircleIcon className='h-10 text-gray-500' />;
        } else if (actionType === 'rejected') {
            return <XCircleIcon className='h-10 text-red-500' />;
        } else if (actionType === 'complete') {
            return <CheckCircleIcon className='h-10 text-green-500' />;
        } else if (actionType === 'ataRequested') {
            return <ExclamationTriangleIcon className='h-10 text-yellow-500' />;
        }
    };

    return (
        <section className='flex flex-col'>
            <AtaRequestModal
                ataRequestModal={ataRequestModal}
                setAtaRequestModal={setAtaRequestModal}
                onClose={() => getReleaseLines(false)}
            />
            {selectedPage === pageNames.RELEASE_LINES ? (
                loadingReleaseLines ? (
                    <div className='flex justify-center items-center h-screen'>
                        <LoadingSpinner className={'w-44'} spinnerSize={'32'} />
                    </div>
                ) : (
                    <div className='px-8'>
                        <header className='flex mt-10 h-10'>
                            <button
                                className='flex justify-center items-center px-3 my-auto w-24 h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                                onClick={clearSelectedRelease}
                            >
                                <ArrowLeftIcon className='mr-2 w-6 h-6' />
                                <span>Back</span>
                            </button>

                            {/* TODO: Add later for the timer functionality */}
                            {/* <div className='ml-auto text-left'>
                            <p>User Time/Part: {userTime}</p>
                            <p>Department Time/Part: {departmentTime}</p>
                            </div> */}
                        </header>
                        <article>
                            <div className='grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-4 items-start pt-8'>
                                {internalComments.length ? (
                                    <div className='min-w-0 whitespace-pre-wrap break-words'>
                                        <div className='flex-col'>
                                            <span className='font-semibold underline'>Internal Comments:</span>
                                        </div>
                                        {internalComments}
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                                <div className='flex flex-col gap-2 justify-center items-center text-center'>
                                    <h1 className='text-3xl font-bold leading-tight'>
                                        Release Lines for{' '}
                                        <a
                                            className='inline-block text-blue-800 underline hover:text-blue-400'
                                            href={releaseData.link}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {releaseName}
                                        </a>
                                    </h1>
                                    <p className='select-none text-base text-gray-600'>Processed: {processedAmount()}</p>
                                </div>
                                <div className='flex justify-end'>
                                    <button
                                        className='px-3 my-auto h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                                        onClick={getPickingLabelsToPrint}
                                        disabled={pickingLabelsLoading}
                                    >
                                        {pickingLabelsLoading ? (
                                            <LoadingSpinner className={'w-44'} spinnerSize={'7'} />
                                        ) : (
                                            'Print All Picking Labels'
                                        )}
                                    </button>
                                </div>
                            </div>

                            <table className='mt-1 w-full'>
                                <thead className='px-2 text-white bg-blue-primary'>
                                    <tr className='border-b'>
                                        <th className='pl-3 w-16 text-left'>Line</th>
                                        <th className='w-32 text-left'>INV</th>
                                        <th className='w-52 text-left'>Part Number</th>
                                        <th className='w-60 text-left'>Part Keyword</th>
                                        <th className='w-60 text-left'>Serial Number</th>
                                        <th className='w-36 text-left'>CC</th>
                                        <th className='w-36 text-left'>Trace</th>
                                        <th className='w-20 text-left'>Qty</th>
                                        <th className='w-28 text-left'>Owner Code</th>
                                        {/* TODO: <th className='w-32 text-left'>Time to Process</th> */}
                                        <th className='w-20 text-left'>Physical</th>
                                        <th className='w-20 text-left'>Trace</th>
                                        <th className='w-20 text-left'>Forms</th>
                                        {hasATAPermissions ? <th className='w-20 text-left'>ATA</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {releaseLineData.map((line) => {
                                        const hasInventory = line.invLineName;

                                        return (
                                            <tr
                                                key={line.releaseLineId}
                                                className='h-10 border-b border-gray-400 odd:bg-gray-200 even:bg-gray-50'
                                            >
                                                <td className='pl-3'>{line.lineNumber}</td>
                                                <td>
                                                    <a
                                                        className='text-blue-800 underline cursor-pointer hover:text-blue-400'
                                                        href={hasInventory ? line.invLineLink : line.rlsLineLink}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                    >
                                                        {hasInventory ? line.invLineName : line.releaseLineName}
                                                    </a>
                                                </td>
                                                <td>{line.partName ?? line.salesOrderProduct}</td>
                                                <td>{line.keyword}</td>
                                                <td>{line.serialNumber}</td>
                                                <td>{line.conditionCodeName}</td>
                                                <td>{line.trace}</td>
                                                <td>{line.quantity}</td>
                                                <td>{line.ownerCodeName}</td>
                                                {/* <td>{line.timeToProcess}</td> */}
                                                <td>
                                                    {hasInventory ? (
                                                        <button
                                                            onClick={() =>
                                                                handlePageChange(pageNames.PHYSICAL, line.releaseLineId)
                                                            }
                                                        >
                                                            {clickableAction(line.physicalStatus)}
                                                        </button>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {hasInventory ? (
                                                        <button
                                                            onClick={() =>
                                                                handlePageChange(pageNames.TRACE, line.releaseLineId)
                                                            }
                                                        >
                                                            {clickableAction(line.traceStatus)}
                                                        </button>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {hasInventory ? (
                                                        <button
                                                            onClick={() =>
                                                                handlePageChange(pageNames.FORMS, line.releaseLineId)
                                                            }
                                                        >
                                                            {clickableAction(line.formsStatus)}
                                                        </button>
                                                    ) : null}
                                                </td>
                                                {hasATAPermissions ? (
                                                    <td>
                                                        {hasInventory ? (
                                                            <div className='relative group inline-block'>
                                                                <input
                                                                    type='checkbox'
                                                                    className={`ml-1.5 scale-150 ${
                                                                        line.ataRequestAllowed
                                                                            ? 'hover:cursor-pointer'
                                                                            : 'opacity-40 cursor-not-allowed'
                                                                    }`}
                                                                    checked={selectedAtaReleaseLineNames.includes(
                                                                        line.releaseLineName
                                                                    )}
                                                                    onChange={(e) =>
                                                                        handleATACheckbox(
                                                                            line.releaseLineName,
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                    disabled={!line.ataRequestAllowed}
                                                                />
                                                                {!line.ataRequestAllowed && line.blockingReleases?.length ? (
                                                                    <BlockingTooltip
                                                                        blockingReleases={line.blockingReleases}
                                                                    />
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                    </td>
                                                ) : null}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {hasATAPermissions ? (
                                <div className='flex justify-end mt-2'>
                                    <button
                                        className='px-3 h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary hover:cursor-pointer disabled:cursor-not-allowed'
                                        onClick={submitATARequestToAirCert}
                                        disabled={!selectedAtaReleaseLineNames.length || ataRequestSubmitting}
                                    >
                                        {ataRequestSubmitting ? (
                                            <LoadingSpinner className={'w-44'} spinnerSize={'7'} />
                                        ) : (
                                            'Send ATA Request'
                                        )}
                                    </button>
                                </div>
                            ) : null}
                        </article>
                    </div>
                )
            ) : null}
            {selectedPage === pageNames.PHYSICAL ? (
                <Physical
                    currentReleaseLine={currentReleaseLine}
                    currentUser={currentUser}
                    handlePageChange={handlePageChange}
                    releaseName={releaseName}
                    releaseId={releaseData.id}
                    returnToReleaseLines={returnToReleaseLines}
                    setShowNotification={setShowNotification}
                />
            ) : null}
            {selectedPage === pageNames.TRACE ? (
                <Trace
                    currentReleaseLine={currentReleaseLine}
                    documentsToPrint={documentsToPrint}
                    setDocumentsToPrint={setDocumentsToPrint}
                    setShowNotification={setShowNotification}
                    releaseName={releaseName}
                    releaseId={releaseData.id}
                    returnToReleaseLines={returnToReleaseLines}
                />
            ) : null}
            {selectedPage === pageNames.FORMS ? (
                <Forms
                    currentReleaseLine={currentReleaseLine}
                    returnToReleaseLines={returnToReleaseLines}
                    releaseData={releaseData}
                    releaseName={releaseName}
                    releaseLineData={releaseLineData}
                />
            ) : null}
        </section>
    );
}

function AtaRequestModal({ ataRequestModal, setAtaRequestModal, onClose }) {
    if (!ataRequestModal.shown) return null;

    const isLoading = ataRequestModal.status === ataRequestModalStatuses.LOADING;
    const isSuccess = ataRequestModal.status === ataRequestModalStatuses.SUCCESS;
    const isError = ataRequestModal.status === ataRequestModalStatuses.ERROR;

    const closeModal = () => {
        if (isLoading) return;
        if (isSuccess && onClose) onClose();
        setAtaRequestModal(defaultAtaRequestModal);
    };

    return (
        <section className='flex fixed inset-0 z-50 justify-center items-center px-4 bg-gray-900 bg-opacity-60'>
            <article className='flex flex-col gap-6 p-8 w-full max-w-xl text-center bg-white rounded-lg shadow-xl'>
                {isLoading ? (
                    <>
                        <header>
                            <h2 className='text-2xl font-semibold text-gray-900'>Submitting ATA Request</h2>
                            <p className='mt-2 text-gray-600'>
                                Processing {ataRequestModal.releaseLineCount} selected release line(s). This may take a
                                moment.
                            </p>
                        </header>
                        <LoadingSpinner className='w-full' spinnerSize='16' />
                    </>
                ) : null}

                {isSuccess ? (
                    <>
                        <header>
                            <CheckCircleIcon className='mx-auto w-12 h-12 text-green-500' />
                            <h2 className='mt-3 text-2xl font-semibold text-gray-900'>ATA Request Complete</h2>
                            <p className='mt-2 text-gray-600'>
                                The ATA request for {ataRequestModal.releaseLineCount} release line(s) completed
                                successfully.
                            </p>
                        </header>
                        <a
                            className='text-lg font-semibold text-blue-800 underline hover:text-blue-400'
                            href={ataRequestModal.releaseLink}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            Open new Release {ataRequestModal.newReleaseName}
                        </a>
                    </>
                ) : null}

                {isError ? (
                    <>
                        <header>
                            <XCircleIcon className='mx-auto w-12 h-12 text-red-500' />
                            <h2 className='mt-3 text-2xl font-semibold text-gray-900'>ATA Request Failed</h2>
                        </header>
                        <p className='text-gray-600'>{ataRequestModal.errorMessage}</p>
                    </>
                ) : null}

                {!isLoading ? (
                    <footer className='flex justify-center'>
                        <button
                            type='button'
                            className='px-4 h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                            onClick={closeModal}
                        >
                            {isSuccess ? 'Close and Refresh' : 'Close'}
                        </button>
                    </footer>
                ) : null}
            </article>
        </section>
    );
}
